import { GlobalContextProvider } from '@/shared/GlobalContext/GlobalContext';
import { SidebarContextProvider } from '@/shared/Page/Sidebar/SidebarContext/SidebarContext';
import { SSOContextProvider } from '@/shared/SSOContext/SSOContext';
import { ThemesContextProvider } from '@/shared/ThemesContext/ThemesContext';
import { StudentContextProvider } from '@/shared/StudentContext/StudentContext';
import Head from 'next/head';
import '../styles/globals.css';
import ColorTokensStyle from '@/shared/ColorTokensStyle/ColorTokensStyle';
import { NavigationContextProvider } from '@/shared/NavigationContext/NavigationContext';
import ErrorBoundary from '@/shared/ErrorBoundary';
import { ScrollContextProvider } from '@/shared/KeepScroll/ScrollContext/ScrollContext';
import { Result } from 'antd';
import Image from 'next/legacy/image';
import { WebsocketsContextProvider } from '@/shared/WebsocketsContext/WebsocketsContext';
import { RealTimeActivityContextProvider } from '@/shared/RealTimeActivityContext/RealTimeActivityContext';
import { AudioAutoPlayerContextProvider } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import { StudentRealTimeActivityContextProvider } from '@/shared/StudentRealTimeActivityContext/StudentRealTimeActivityContext';
import { useEffect } from 'react';
import { FullscreenContextProvider } from '@/shared/FullscreenContext/FullscreenContext';
import 'react-aspect-ratio/aspect-ratio.css';
import { setupAxiosCache } from '@/api/axiosInstance';
import { GamificationContextProvider } from '@/shared/Gamification/GamificationContext/GamificationContext';
import PlausibleAnalytics from '@/shared/PlausibleAnalytics/PlausibleAnalytics';
import EnvironmentWall from '@/shared/EnvironmentWall/EnvironmentWall';
import { QueryClientProvider } from '@tanstack/react-query';
import useQueryClientFactory from '@/v2/conf/queryClient/hooks/useQueryClientFactory.hook';
import OnQueryClientReady from '@/v2/conf/queryClient/components/OnQueryClientReady';
import useAcquisitionAnalytics from '@/v2/domains/Analytics/hooks/useAcquisitionAnalytics.hook';
import ExpiredSubscriptionModal from '@/v2/domains/Subscriptions/components/ExpiredSubscriptionModal/ExpiredSubscriptionModal';
import FlowAnalyticsEventsQueueManager from '@/v2/domains/Analytics/components/FlowAnalyticsEventsQueueManager/FlowAnalyticsEventsQueueManager';
import FloatingSurvey from '@/v2/domains/Surveys/components/FloatingSurvey/FloatingSurvey';
import EmailVerificationGate from '@/v2/domains/Auth/components/EmailVerificationGate/EmailVerificationGate';

const maintenanceMode = false;

export default function MyApp({ Component, ...pageProps }) {
  const { queryClient } = useQueryClientFactory();

  useAcquisitionAnalytics();
  useEffect(() => {
    // Polyfill for queueMicrotask
    if (typeof window.queueMicrotask !== 'function') {
      window.queueMicrotask = function (callback) {
        Promise.resolve()
          .then(callback)
          .catch((e) =>
            setTimeout(() => {
              throw e;
            })
          );
      };
    }

    setupAxiosCache();
  }, []);

  return (
    <ErrorBoundary>
      <Head>
        <title>Rockalingua</title>
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href="/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/favicon/safari-pinned-tab.svg"
          color="#00BCEB"
        />
        <meta name="theme-color" content="#ffffff" />
      </Head>
      <ColorTokensStyle />

      {maintenanceMode ? (
        <Result
          icon={
            <Image
              src={
                process.env.NEXT_PUBLIC_CLOUDFRONT_STATIC_HOST + '/img/logo.png'
              }
              alt="Rockalingua logo"
              width={258}
              height={77}
            />
          }
          title="We are preparing new things..."
          subTitle="Rockalingua will be ready in a bit"
        />
      ) : (
        <QueryClientProvider client={queryClient}>
          <PlausibleAnalytics>
            <GlobalContextProvider>
              <FlowAnalyticsEventsQueueManager>
                <OnQueryClientReady />
                <FullscreenContextProvider>
                  <ThemesContextProvider>
                    <SSOContextProvider>
                      <SidebarContextProvider>
                        <StudentContextProvider>
                          <NavigationContextProvider>
                            <ScrollContextProvider>
                              <AudioAutoPlayerContextProvider>
                                <WebsocketsContextProvider>
                                  <RealTimeActivityContextProvider>
                                    <StudentRealTimeActivityContextProvider>
                                      <GamificationContextProvider>
                                        <EnvironmentWall />
                                        <ExpiredSubscriptionModal />
                                        <FloatingSurvey />
                                        <EmailVerificationGate>
                                          <Component {...pageProps} />
                                        </EmailVerificationGate>
                                      </GamificationContextProvider>
                                    </StudentRealTimeActivityContextProvider>
                                  </RealTimeActivityContextProvider>
                                </WebsocketsContextProvider>
                              </AudioAutoPlayerContextProvider>
                            </ScrollContextProvider>
                          </NavigationContextProvider>
                        </StudentContextProvider>
                      </SidebarContextProvider>
                    </SSOContextProvider>
                  </ThemesContextProvider>
                </FullscreenContextProvider>
              </FlowAnalyticsEventsQueueManager>
            </GlobalContextProvider>
          </PlausibleAnalytics>
        </QueryClientProvider>
      )}
    </ErrorBoundary>
  );
}
